"use client"
import { Collapse } from "antd";
import ReportHeader from "./response-display/ReportHeader";
import PatientOverview from "./response-display/PatientOverview";
import ClinicalAnalysis from "./response-display/ClinicalAnalysis";
import DifferentialDiagnosis from "./response-display/DifferentialDiagnosis";
import ManagementPlan from "./response-display/ManagementPlan";
import ClinicalQuestions from "./response-display/ClinicalQuestions";
import References from "./response-display/References";
import ConfidenceAssessment from "./response-display/ConfidenceAssessment";
import FollowUpQuestion from "./response-display/FollowUpQuestion";

const { Panel } = Collapse;

export default function ResponseDisplay({ response }) {
    if (!response) return null;

    const parseIfJsonString = (value) => {
        if (typeof value !== "string") return value;
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    };

    const reportSource = response?.physician_report || response?.report_json || response?.report || null;
    const report = parseIfJsonString(reportSource) || reportSource;
    if (!report || typeof report !== "object") return null;

    const safeReport = {
        report_metadata: report?.report_metadata || {},
        patient_overview: report?.patient_overview || {},
        clinical_analysis: {
            ...(report?.clinical_analysis || {}),
            lab_findings: Array.isArray(report?.clinical_analysis?.lab_findings)
                ? report.clinical_analysis.lab_findings
                : [],
            diagnostic_reasoning: Array.isArray(report?.clinical_analysis?.diagnostic_reasoning)
                ? report.clinical_analysis.diagnostic_reasoning
                : []
        },
        differential_diagnosis: Array.isArray(report?.differential_diagnosis)
            ? report.differential_diagnosis
            : Array.isArray(report?.differential_diagnos)
                ? report.differential_diagnos
            : [],
        clinical_questions_for_workup: {
            ...(report?.clinical_questions_for_workup || {}),
            history_questions: Array.isArray(report?.clinical_questions_for_workup?.history_questions)
                ? report.clinical_questions_for_workup.history_questions
                : [],
            physical_exam_focus: Array.isArray(report?.clinical_questions_for_workup?.physical_exam_focus)
                ? report.clinical_questions_for_workup.physical_exam_focus
                : []
        },
        references: Array.isArray(report?.references) ? report.references : [],
        confidence_assessment: report?.confidence_assessment || {},
        management_plan: {
            ...(report?.management_plan || {}),
            immediate_actions: Array.isArray(report?.management_plan?.immediate_actions)
                ? report.management_plan.immediate_actions
                : [],
            pharmacological: Array.isArray(report?.management_plan?.pharmacological)
                ? report.management_plan.pharmacological.map((item) => ({
                    ...(item || {}),
                    first_line: Array.isArray(item?.first_line) ? item.first_line : [],
                    second_line: Array.isArray(item?.second_line) ? item.second_line : [],
                    third_line: Array.isArray(item?.third_line) ? item.third_line : [],
                    escalation_criteria: Array.isArray(item?.escalation_criteria) ? item.escalation_criteria : [],
                    de_escalation_criteria: Array.isArray(item?.de_escalation_criteria) ? item.de_escalation_criteria : []
                }))
                : [],
            non_pharmacological: Array.isArray(report?.management_plan?.non_pharmacological)
                ? report.management_plan.non_pharmacological
                : [],
            investigations_ordered: Array.isArray(report?.management_plan?.investigations_ordered)
                ? report.management_plan.investigations_ordered
                : [],
            follow_up: report?.management_plan?.follow_up || {},
            patient_education: Array.isArray(report?.management_plan?.patient_education)
                ? report.management_plan.patient_education
                : [],
            red_flags_to_watch: Array.isArray(report?.management_plan?.red_flags_to_watch)
                ? report.management_plan.red_flags_to_watch
                : []
        }
    };

    return (
        <div className="w-full">
            <style>{`
                .report-collapse .ant-collapse-item {
                    background: #eff6ff !important;
                    border: 1px solid #bfdbfe !important;
                    border-radius: 8px !important;
                    margin-bottom: 12px !important;
                }
                .report-collapse .ant-collapse-header {
                    background: transparent !important;
                    padding: 16px 20px !important;
                    border-radius: 8px !important;
                }
                .report-collapse .ant-collapse-content {
                    background: transparent !important;
                    border-top: none !important;
                }
                .report-collapse .ant-collapse-content-box {
                    padding: 0 20px 20px 20px !important;
                }
                .report-collapse .ant-collapse-expand-icon {
                    color: #0B7BC1 !important;
                }
            `}</style>
            <div className="">
                {/* Always visible header */}
                <ReportHeader reportMetadata={safeReport.report_metadata}  />
                
                {/* Collapsible sections - all closed by default */}
                <Collapse
                    defaultActiveKey={[]}
                    expandIconPosition="end"
                    className="mb-4 report-collapse"
                    style={{
                        background: 'transparent',
                        border: 'none'
                    }}
                >
                    <Panel 
                        header={<span className="text-base font-semibold">Patient Overview</span>} 
                        key="patient"
                    >
                        <PatientOverview patientOverview={safeReport.patient_overview} />
                    </Panel>

                    <Panel 
                        header={<span className="text-base font-semibold">Clinical Analysis</span>} 
                        key="clinical"
                    >
                        <ClinicalAnalysis clinicalAnalysis={safeReport.clinical_analysis} />
                    </Panel>

                    <Panel 
                        header={<span className="text-base font-semibold">Differential Diagnosis</span>} 
                        key="differential"
                    >
                        <DifferentialDiagnosis differentialDiagnosis={safeReport.differential_diagnosis} />
                    </Panel>

                    <Panel 
                        header={<span className="text-base font-semibold">Management Plan</span>} 
                        key="management"
                    >
                        <ManagementPlan managementPlan={safeReport.management_plan} />
                    </Panel>

                    <Panel 
                        header={<span className="text-base font-semibold">Clinical Questions for Workup</span>} 
                        key="questions"
                    >
                        <ClinicalQuestions clinicalQuestions={safeReport.clinical_questions_for_workup} />
                    </Panel>

                    <Panel 
                        header={<span className="text-base font-semibold">References</span>} 
                        key="references"
                    >
                        <References references={safeReport.references} />
                    </Panel>

                    <Panel 
                        header={<span className="text-base font-semibold">Confidence Assessment</span>} 
                        key="confidence"
                    >
                        <ConfidenceAssessment confidenceAssessment={safeReport.confidence_assessment} />
                    </Panel>
                </Collapse>

                {/* Always visible follow-up question */}
                <FollowUpQuestion response={response} />
            </div>
        </div>
    );
}
